import Decimal from 'decimal.js-light';
import _ from 'lodash';
import type { ReactNode } from 'react';

import { cleanRawDecimalValue } from '@adflow/shared/util-formatting';

import { hasValue } from '../util/hasValue';

import { Diff } from './Diff';
import { HistoryEntryFieldLayout } from './HistoryEntryFieldLayout';
import { renderHistoryDisplayValue } from './renderHistoryDisplayValue';

function isEmptyDecimal(value: unknown) {
	try {
		const cleanedValue = cleanRawDecimalValue(value);
		return new Decimal(cleanedValue).isZero();
	} catch {
		return false;
	}
}

export interface HistoryEntryFieldDefaultProps {
	label: string;
	oldValue: ReactNode;
	newValue: ReactNode;
}
export function HistoryEntryFieldDefault({
	label,
	oldValue,
	newValue,
}: HistoryEntryFieldDefaultProps) {
	if (!hasValue(oldValue) && !hasValue(newValue)) {
		return null;
	}
	if (oldValue === '' && newValue === '') {
		return null;
	}
	if (!hasValue(oldValue) && (isEmptyDecimal(newValue) || newValue === '')) {
		// No old value, and new value is 0 or ''; this is
		// likely a default value on a new entity, so don't
		// show it.
		return null;
	}
	if (_.isEqual(oldValue, newValue)) {
		// Values are deeply equal, so don't show a diff.
		return null;
	}

	const isDiff = hasValue(oldValue) && newValue !== oldValue;
	return (
		<HistoryEntryFieldLayout label={label}>
			{isDiff ? (
				<Diff oldValue={oldValue} newValue={newValue} />
			) : (
				renderHistoryDisplayValue(newValue)
			)}
		</HistoryEntryFieldLayout>
	);
}
